import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '@/app/providers/AuthProvider';
import { ApiError } from '@/shared/services/api-error';
import {
  blockedDetailsSchema,
  credentialsSchema,
  invalidCredentialsDetailsSchema,
  type Credentials,
  type LoginOutcome,
  type TwoFactorRequiredOutcome,
} from '../models/auth.model';
import * as authService from '../services/auth.service';
import { useLoginBlock, type LoginBlock } from './useLoginBlock';

/** Mensagem exibida no cartão de aviso da tela. */
export interface LoginNotice {
  variant: 'credential' | 'blocked' | 'error';
  title: string;
  body: string;
  /** Tentativas restantes, quando a API informa. */
  attemptsRemaining?: number;
}

export interface LoginViewModel {
  form: UseFormReturn<Credentials>;
  submit: () => void;
  isSubmitting: boolean;
  notice: LoginNotice | null;
  dismissNotice: () => void;
  block: LoginBlock;
  /** Desafio em curso, quando o dispositivo precisa ser verificado. */
  challenge: TwoFactorRequiredOutcome | null;
  /** Conclui o login depois do segundo fator. */
  completeChallenge: (outcome: LoginOutcome) => void;
  cancelChallenge: () => void;
  forgotPasswordOpen: boolean;
  openForgotPassword: () => void;
  closeForgotPassword: () => void;
}

/**
 * Estado e regras da tela de login.
 *
 * Toda a lógica vive aqui: a View recebe estado pronto e devolve eventos. Isso
 * torna a tela testável sem renderizar nada — e mantém a View sem `fetch`, sem
 * montagem de requisição e sem decisão de negócio.
 */
export function useLoginViewModel(): LoginViewModel {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const block = useLoginBlock();

  const [notice, setNotice] = useState<LoginNotice | null>(null);
  const [challenge, setChallenge] = useState<TwoFactorRequiredOutcome | null>(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const form = useForm<Credentials>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
    // Valida no blur, não a cada tecla: corrigir um e-mail enquanto se digita é
    // ruído, não ajuda.
    mode: 'onBlur',
  });

  const finish = useCallback(
    (outcome: LoginOutcome) => {
      if (outcome.status === 'two_factor_required') {
        setChallenge(outcome);
        return;
      }

      signIn(outcome);
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      void navigate(from ?? '/agenda', { replace: true });
    },
    [signIn, navigate, location.state],
  );

  const submit = form.handleSubmit(async (credentials) => {
    setNotice(null);

    try {
      finish(await authService.login(credentials));
    } catch (error) {
      setNotice(toNotice(error, block.start));
    }
  });

  return {
    form,
    submit: () => void submit(),
    isSubmitting: form.formState.isSubmitting,
    notice,
    dismissNotice: () => setNotice(null),
    block,
    challenge,
    completeChallenge: finish,
    cancelChallenge: () => setChallenge(null),
    forgotPasswordOpen,
    openForgotPassword: () => setForgotPasswordOpen(true),
    closeForgotPassword: () => setForgotPasswordOpen(false),
  };
}

/** Traduz a falha da API no aviso que a tela exibe. */
function toNotice(
  error: unknown,
  startBlock: (retryAfterSeconds: number, blockMinutes: number) => void,
): LoginNotice {
  if (!(error instanceof ApiError)) {
    return {
      variant: 'error',
      title: 'Não foi possível entrar',
      body: 'Tente novamente em instantes.',
    };
  }

  if (error.code === 'LOGIN_ATTEMPTS_EXCEEDED') {
    const details = blockedDetailsSchema.safeParse(error.details);
    if (details.success) startBlock(details.data.retryAfter, details.data.blockMinutes);

    return {
      variant: 'blocked',
      title: 'Muitas tentativas',
      body: 'Por segurança, o acesso a esta conta foi pausado. Tente novamente quando a contagem terminar.',
    };
  }

  if (error.code === 'INVALID_CREDENTIALS') {
    const details = invalidCredentialsDetailsSchema.safeParse(error.details);
    return {
      variant: 'credential',
      title: 'E-mail ou senha incorretos',
      body: 'Confira os dados e tente de novo.',
      attemptsRemaining: details.success ? details.data.attemptsRemaining : undefined,
    };
  }

  return { variant: 'error', title: 'Não foi possível entrar', body: error.message };
}
